import React, { useState, useContext } from 'react';
import { createPortal } from 'react-dom';
import styled, { ThemeContext } from 'styled-components';
import { debounce } from 'lodash';
import { userService } from '../../services/userService';
import VipIcon from '../Icons/VipIcons';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';
import { 
  PrimaryButton, 
  Input, 
  Avatar,
  Loading,
  Modal,
  TaggedCreators
} from '../common';

// Styled Components
const CreatorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  gap: 12px;
  max-height: 400px;
  overflow-y: auto;
  padding: 8px 0;
`;

const CreatorCard = styled.div`
  display: flex;
  align-items: center;
  padding: 12px;
  border: 1px solid ${props => props.$selected ? props.theme.colors.primary : props.theme.colors.border};
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  background: ${props => props.$selected ? 
    `${props.theme.colors.primary}20` : 
    props.theme.colors.background
  };

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  }
`;

const CreatorInfo = styled.div`
  margin-left: 8px;
  flex: 1;
  min-width: 0;
`;

const CreatorName = styled.div`
  font-weight: 600;
  font-size: 12px;
  color: ${props => props.theme.colors.text};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const CreatorUsername = styled.div`
  font-size: 10px;
  color: ${props => props.theme.colors.textMuted};
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const FooterContent = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const SelectedCount = styled.div`
  color: ${props => props.theme.colors.textSecondary};
  font-size: 14px;
`;

const RoundCheckbox = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid ${props => props.$checked ? props.theme.colors.primary : props.theme.colors.border};
  background: ${props => props.$checked ? props.theme.colors.primary : 'transparent'};
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  flex-shrink: 0;
  outline: none;

  &:hover {
    border-color: ${props => props.theme.colors.primary};
    transform: scale(1.1);
  }

  &:focus {
    box-shadow: 0 0 0 2px ${props => props.theme.colors.primary}40;
  }
`;

/**
 * Standalone tagging modal that can be integrated into any text editor
 * Usage: Add this component alongside existing TipTap editors and call onUserSelect
 * when a user is selected to insert the mention into the editor
 * 
 * Can also be used to manage existing tags with selectedUsers and onRemoveUser props
 */
const UserTaggingModal = ({ 
  isOpen, 
  onClose, 
  onUserSelect,      // Called when adding a new user (single user)
  onSelectUser,      // Alias for onUserSelect (for compatibility)
  onSelect,          // Called with array of selected users on Done
  onRemoveUser,      // Called when removing an existing user
  selectedUsers = [],// Existing tagged users to display
  currentUser,
  title = "Tag Verified Creators",
  subtitle = ""
}) => {
  // Use onSelectUser if onUserSelect not provided (for compatibility)
  const handleUserSelect = onUserSelect || onSelectUser;
  
  const theme = useContext(ThemeContext);
  const [creators, setCreators] = useState([]);
  const [loadingCreators, setLoadingCreators] = useState(false);
  const [creatorSearchQuery, setCreatorSearchQuery] = useState('');
  const [selectedCreators, setSelectedCreators] = useState([]);

  // Load verified creators for modal (only those who allow tagging) with search
  const loadCreators = React.useCallback(async (query = '') => {
    setLoadingCreators(true);
    try {
      const result = await userService.searchCreatorsForTagging(query);
      // Handle paginated response - extract content array
      const creatorList = result.content || result;
      setCreators(creatorList.filter(creator => creator.id !== currentUser?.id));
    } catch (error) {
      console.error('Error loading creators:', error);
      setCreators([]);
    } finally {
      setLoadingCreators(false);
    }
  }, [currentUser]);

  // Debounced creator search (only if 3+ chars)
  const debouncedCreatorSearch = React.useCallback(
    debounce((query) => {
      if (query.trim().length >= 3) {
        loadCreators(query);
      } else {
        setCreators([]);
      }
    }, 300),
    [loadCreators]
  );

  // Handle search input change
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setCreatorSearchQuery(query);
    debouncedCreatorSearch(query);
  };

  // Reset when modal opens - don't load on open
  React.useEffect(() => {
    if (isOpen) {
      setCreatorSearchQuery('');
      setSelectedCreators([]);
      setCreators([]); // Don't load on open - user must search
    }
  }, [isOpen]);

  // Filter out already tagged users from search results
  const filteredCreators = creators.filter(creator => 
    !selectedUsers?.some(u => u.id === creator.id)
  );

  const handleCreatorToggle = (creator) => {
    setSelectedCreators(prev => {
      const isSelected = prev.some(c => c.id === creator.id);
      if (isSelected) {
        return prev.filter(c => c.id !== creator.id);
      } else {
        return [...prev, creator];
      }
    });
  };

  const handleDone = () => {
    // If onSelect is provided, call it with all selected creators as an array
    if (onSelect && selectedCreators.length > 0) {
      onSelect(selectedCreators);
    } else if (handleUserSelect) {
      // Fall back to calling handleUserSelect for each selected creator individually
      selectedCreators.forEach(creator => handleUserSelect(creator));
    }
    onClose();
  };

  useBodyScrollLock(isOpen);

  // Handle escape key
  React.useEffect(() => {
    if (!isOpen) return undefined;
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  // Render the modal using a portal to ensure it appears at the root level
  return createPortal(
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      subtitle={subtitle}
      maxWidth="600px"
      footer={
        <FooterContent>
          <SelectedCount>
            {selectedCreators.length} creator{selectedCreators.length !== 1 ? 's' : ''} selected
          </SelectedCount>
          <PrimaryButton
            onClick={handleDone}
            disabled={selectedCreators.length === 0}
            $size="small"
            $rounded
          >
            Done
          </PrimaryButton>
        </FooterContent>
      }
    >
      {/* Show existing tagged users using TaggedCreators component */}
      {selectedUsers && selectedUsers.length > 0 && (
        <div style={{ marginBottom: '16px' }}>
          <div style={{ fontSize: '12px', color: theme.colors.textMuted || '#666', marginBottom: '8px' }}>
            Currently Tagged ({selectedUsers.length})
          </div>
          <TaggedCreators
            creators={selectedUsers}
            onRemoveCreator={onRemoveUser ? (userId) => {
              // Pass userId directly - handlers expect the ID, not the user object
              onRemoveUser(userId);
            } : undefined}
            readOnly={!onRemoveUser}
          />
        </div>
      )}
      
      <Input
        type="text"
        placeholder="Search creators to tag..."
        value={creatorSearchQuery}
        onChange={handleSearchChange}
        autoFocus
        marginBottom="16px"
      />
      
      {loadingCreators ? (
        <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
          <Loading />
        </div>
      ) : (
        <CreatorGrid>
          {filteredCreators.map((creator) => {
            const isSelected = selectedCreators.some(c => c.id === creator.id);
            return (
              <CreatorCard
                key={creator.id}
                onClick={() => handleCreatorToggle(creator)}
                $selected={isSelected}
              >
                <RoundCheckbox
                  $checked={isSelected}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleCreatorToggle(creator);
                  }}
                  role="checkbox"
                  aria-checked={isSelected}
                  tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      handleCreatorToggle(creator);
                    }
                  }}
                >
                  {isSelected && (
                    <VipIcon 
                      name="Checkmark" 
                      size={12} 
                      style={{ color: 'white' }}
                    />
                  )}
                </RoundCheckbox>
                <Avatar
                  src={creator.profilePicture || '/default-avatar.png'}
                  name={creator.fullName || creator.username}
                  alt={creator.username}
                  size="32px"
                  userId={creator.id}
                  style={{ marginLeft: '12px' }}
                  hideOnlineIndicator={true}
                />
                <CreatorInfo>
                  <CreatorName>
                    {creator.fullName || creator.username}
                    {creator.verified && (
                      <VipIcon 
                        name="VerifiedBadge" 
                        size={14} 
                        stroke={theme.colors.primaryDark}
                        style={{ marginLeft: '4px', verticalAlign: 'middle' }}
                      />
                    )}
                  </CreatorName>
                  <CreatorUsername>@{creator.username}</CreatorUsername>
                </CreatorInfo>
              </CreatorCard>
            );
          })}
        </CreatorGrid>
      )}
      
      {!loadingCreators && filteredCreators.length === 0 && (
        <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
          {creatorSearchQuery.length >= 3 
            ? 'No creators found matching your search' 
            : 'Start typing to search creators'}
        </div>
      )}
    </Modal>,
    document.body // Render at the root level of the document
  );
};

/**
 * Button component that triggers the user tagging modal
 * Can be placed anywhere in the UI to trigger creator tagging
 */
export const TagCreatorButton = ({ 
  onUserSelect, 
  currentUser, 
  disabled = false,
  children = "@ Tag Creator",
  ...props 
}) => {
  const [showModal, setShowModal] = useState(false);

  const handleUserSelect = (user) => {
    onUserSelect(user);
    setShowModal(false);
  };

  return (
    <>
      <PrimaryButton
        type="button"
        onClick={() => setShowModal(true)}
        disabled={disabled}
        $size="small"
        {...props}
      >
        {children}
      </PrimaryButton>
      
      <UserTaggingModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        onUserSelect={handleUserSelect}
        currentUser={currentUser}
      />
    </>
  );
};

export default UserTaggingModal;
